import { useState } from 'react';
import toast from 'react-hot-toast';
import { getContactApiUrl } from '../config/globalConfig';
import SuccessModal from './SuccessModal';

export default function ContactForm({ onSuccess }) {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        notRobot: false
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    // Validation functions
    const validateField = (name, value) => {
        let error = '';

        switch (name) {
            case 'name':
                if (!value.trim()) error = 'Name is required';
                else if (value.trim().length < 2) error = 'Name must be at least 2 characters';
                else if (!/^[a-zA-Z\s]+$/.test(value.trim())) error = 'Name can only contain letters and spaces';
                break;

            case 'email':
                if (!value.trim()) error = 'Email is required';
                else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) error = 'Please enter a valid email address';
                break;

            case 'phone':
                if (!value.trim()) error = 'Phone number is required';
                else if (!/^[0-9+\-\s()]{10,15}$/.test(value)) error = 'Please enter a valid phone number';
                break;

            case 'message':
                if (!value.trim()) error = 'Message is required';
                else if (value.trim().length < 10) error = 'Message must be at least 10 characters';
                else if (value.trim().length > 1000) error = 'Message must be less than 1000 characters';
                break;

            case 'notRobot':
                if (!value) error = 'Please confirm you are not a robot';
                break;
        }

        return error;
    };

    // Handle input changes with validation
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        // Update form data
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        // Validate field and update errors
        const error = validateField(name, newValue);
        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    // Handle number-only input for phone
    const handlePhoneInput = (e) => {
        const { name, value } = e.target;
        // Remove non-numeric characters except +, -, (, ), and spaces
        const numericValue = value.replace(/[^0-9+\-\s()]/g, '');

        setFormData(prev => ({
            ...prev,
            [name]: numericValue
        }));

        // Validate field
        const error = validateField(name, numericValue);
        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    // Validate entire form
    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        // Validate all required fields
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) {
                newErrors[key] = error;
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form before submission
        if (!validateForm()) {
            // toast.error('Please fix the errors before submitting');
            return;
        }

        setIsSubmitting(true);

        try {
            const formDataToSend = new FormData();

            // Append all form data
            Object.keys(formData).forEach(key => {
                formDataToSend.append(key, formData[key]);
            });

            // Submit to API
            const response = await fetch(getContactApiUrl('SEND_MESSAGE'), {
                method: 'POST',
                body: formDataToSend
            });

            if (response.ok) {
                // Call onSuccess callback if provided, otherwise show success modal
                if (onSuccess) {
                    onSuccess();
                } else {
                    setShowSuccessModal(true);
                }
                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    message: '',
                    notRobot: false
                });
                setErrors({});
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to send message');
            }
        } catch (error) {
            console.error('Contact form error:', error);
            toast.error(error.message || 'Failed to send message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="contact-form">
                <form className="contact-form-content" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className={`form-input contact_input ${errors.name ? 'error' : ''}`}
                            placeholder="Name"
                        />
                    </div>

                    <div className="form-group">
                        <input
                            type="text"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={`form-input contact_input ${errors.email ? 'error' : ''}`}
                            placeholder="Email"
                        />
                    </div>

                    <div className="form-group">
                        <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handlePhoneInput}
                            className={`form-input contact_input ${errors.phone ? 'error' : ''}`}
                            placeholder="Phone Number"
                        />
                    </div>

                    <div className="form-group">
                        <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            className={`form-input message-field contact_input ${errors.message ? 'error' : ''}`}
                            placeholder="Message"
                            rows={5}
                        />
                    </div>

                    <div className="form-group">
                        <div className="recaptcha-container">
                            <div className="recaptcha-checkbox">
                                <input
                                    type="checkbox"
                                    id="recaptcha"
                                    name="notRobot"
                                    checked={formData.notRobot}
                                    onChange={handleInputChange}
                                    className="contact_input"
                                />
                                <label htmlFor="recaptcha">I'm not a robot</label>
                            </div>
                            <div className="recaptcha-logo">
                                <span>reCAPTCHA</span>
                            </div>
                        </div>
                        {errors.notRobot && <div className="error-message contact_error">{errors.notRobot}</div>}
                    </div>

                    <div className="form-group">
                        <button
                            type="submit"
                            className={`submit-btn ${isSubmitting ? 'submit-btn-loading' : ''}`}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="loading-spinner"></span>
                                    SENDING...
                                </>
                            ) : (
                                'Send Message'
                            )}
                        </button>
                    </div>
                </form>
            </div>
            
            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                title="Thank You!"
                message="We'll get back to you as soon as possible."
            />
        </>
    );
}